import GetAppIcon from '@mui/icons-material/GetApp';
import {Box, FormControlLabel, FormGroup, Radio, RadioGroup, Stack, Switch, capitalize, styled} from '@mui/material';
import Button from '@mui/material/Button';
import axios from 'axios';
import {
  GrowthModelResponseAdjustmentTypeEnum,
  GrowthModelResponseTypeEnum,
  GrowthModelTypesResponseTypesEnum,
} from 'client';
import {UploadButton} from 'components/Generic/Buttons/UploadButton';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import GenericTextField from 'components/Generic/TextField';
import {handleKeyDown} from 'components/InputMenu/helpers';
import {MonthsSinceReleaseTable} from 'components/Models/MonthsSinceReleaseTable';
import {useInputMenuContext} from 'context/InputMenu';
import useAddEditGrowthModel from 'hooks/addEdit/useAddEditGrowthModel';
import {methodType, ITTTVersionEnum} from 'interfaces';
import FileDownload from 'js-file-download';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {StyledAdornedEnd, StyledHelperTypography} from 'styles/InputMenu';
import {getBaseUrl} from '../../utils/runtimeVars';
import InputActionButtons from './InputActionButtons';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {EMPTY_ID, useGetGrowthModelsTypes} from '../../hooks/swr';
import {useFlags} from 'launchdarkly-react-client-sdk';
import useParseURL from 'hooks/useParseURL';
import {sanitizeFileName} from 'utils/sanitizeFilename';

const BASE_URL = getBaseUrl('');
const EXCEL_EXTENSION = '.xlsx';
const MONTHS_AFTER_STOCKING = 24;
const CALENDER_YEAR = 36;

const ITTTVersions = [ITTTVersionEnum.vest, ITTTVersionEnum.midt, ITTTVersionEnum.orret, ITTTVersionEnum.nord];

function GrowthModel() {
  const {createUpdateGrowthModel, setCreateUpdateGrowthModel, growthModel, setGrowthModel, method, growthModelId} =
    useAddEditGrowthModel();
  const {t} = useTranslation('common');
  const {planId} = useParseURL();
  const {data: modelTypes} = useGetGrowthModelsTypes({resourceId: EMPTY_ID});

  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {enableNord2ItttModel, gammonGrowthModel: enableGammonGrowthModel} = useFlags();

  const downloadFile = useCallback(async () => {
    const {url, fileName} = getSGRFile(growthModel.name || 'unknown', method, growthModelId, planId);
    const _method = 'GET';
    void axios
      .request({
        url,
        method: _method,
        responseType: 'blob',
      })
      .then((response) => {
        const data = response.data;
        FileDownload(data, `${sanitizeFileName(fileName)}${EXCEL_EXTENSION}`);
      });
  }, [growthModelId, growthModel.name]);

  const ITTTSelected = growthModel.type === 'ittt';

  if (enableNord2ItttModel && !ITTTVersions.includes(ITTTVersionEnum.nord2)) {
    ITTTVersions.push(ITTTVersionEnum.nord2);
  }

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack spacing={2}>
        {/* Growth Model Name */}
        <GenericTextField
          label={t('name')}
          value={growthModel.name}
          onChange={(event) => {
            setGrowthModel({...growthModel, name: event.target.value});
          }}
          aria-label={t('name')}
          slotProps={{htmlInput: {'data-testid': 'name'}}}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateGrowthModel);
          }}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={growthModel.isDefault}
                onChange={(event) => {
                  setGrowthModel({...growthModel, isDefault: event.target.checked});
                }}
              />
            }
            label={t('default')}
          />
        </FormGroup>

        {/* Model Type Picker */}
        <GenericSelect
          disabled={method === methodType.edit}
          value={growthModel.type}
          onChange={(event) => {
            const type = event.target.value as GrowthModelResponseTypeEnum;
            setGrowthModel({...growthModel, type});
          }}
          label={t('modelType')}
          data-testid="model-type"
        >
          {modelTypes?.types
            ?.filter((type) => enableGammonGrowthModel || type !== GrowthModelTypesResponseTypesEnum.Gammon)
            .map((model) => (
              <GenericSelectItem value={model} key={model}>
                {model}
              </GenericSelectItem>
            ))}
        </GenericSelect>

        {/* ITTT version Picker */}
        {ITTTSelected && (
          <GenericSelect
            value={growthModel.version}
            onChange={(event) => {
              const version = event.target.value as ITTTVersionEnum;
              setGrowthModel({...growthModel, version});
            }}
            label={t('ITTTVersion')}
            data-testid="ITTT-version"
          >
            {ITTTVersions.map((version) => (
              <GenericSelectItem value={version} key={version}>
                {capitalize(t(version))}
              </GenericSelectItem>
            ))}
          </GenericSelect>
        )}

        {/* SGR File uploading/downloading */}
        {growthModel.type === GrowthModelResponseTypeEnum.Sgr && (
          <>
            <StyledHelperTypography fontSize="fontSize">{t('uploadSGRFromFile').toUpperCase()}</StyledHelperTypography>
            <Box display="flex" justifyContent="space-between">
              <Box width="45%">
                <UploadButton
                  fullWidth={true}
                  onFileSelection={(file) => {
                    setGrowthModel({...growthModel, file});
                  }}
                  data-testid="upload-sgr-model"
                />
              </Box>
              <Box width="45%">
                <Button
                  color="secondary"
                  variant="contained"
                  component="label"
                  fullWidth={true}
                  endIcon={<GetAppIcon />}
                  onClick={downloadFile}
                >
                  {growthModelId ? t('download') : t('template')}
                </Button>
              </Box>
            </Box>
          </>
        )}
        <Box my={1}>
          <RadioGroup
            row
            value={growthModel.adjustmentType}
            onChange={(event) => {
              const adjustmentType = event.target.value as GrowthModelResponseAdjustmentTypeEnum;
              setGrowthModel({...growthModel, adjustment: initialAdjustments(adjustmentType), adjustmentType});
            }}
          >
            <FormControlLabel
              value={GrowthModelResponseAdjustmentTypeEnum.Constant}
              control={<Radio data-testid="adjustment-constant" />}
              label={t('growthModelAdjustmentConstant')}
            />
            <FormControlLabel
              value={GrowthModelResponseAdjustmentTypeEnum.MonthsSinceRelease}
              control={<Radio data-testid="adjustment-monthly" />}
              label={t('growthModelAdjustmentMonthly')}
            />
            <FormControlLabel
              value={GrowthModelResponseAdjustmentTypeEnum.CalendarYear}
              control={<Radio data-testid="adjustment-yearly" />}
              label={t('growthModelAdjustmentYearly')}
            />
          </RadioGroup>
        </Box>
        {/* Growth Model Adjustment */}

        {growthModel.adjustmentType === GrowthModelResponseAdjustmentTypeEnum.Constant ? (
          <GenericTextField
            label={t('growthModelAdjustment')}
            value={growthModel.adjustment?.[0] ?? 0}
            onChange={(event) => {
              setGrowthModel({...growthModel, adjustment: [parseInt(event.target.value)]});
            }}
            onKeyDown={(event) => {
              inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateGrowthModel);
            }}
            aria-label={t('growthModelAdjustment')}
            slotProps={{
              input: {
                inputComponent: NumberFormatCustom as (props) => JSX.Element,
                endAdornment: <StyledAdornedEnd>%</StyledAdornedEnd>,
              },
              inputLabel: {shrink: true},
              htmlInput: {'data-testid': 'adjustment'},
            }}
          />
        ) : (
          <ScrollableDiv>
            <MonthsSinceReleaseTable
              adjustments={growthModel.adjustment ?? initialAdjustments(growthModel.adjustmentType)}
              onChange={(newAdjustment) => {
                setGrowthModel({
                  ...growthModel,
                  adjustment: newAdjustment === undefined ? [0] : (newAdjustment as number[]),
                });
              }}
              adjustmentType={growthModel.adjustmentType}
            />
          </ScrollableDiv>
        )}

        <InputActionButtons
          loading={createUpdateGrowthModel}
          disabled={!growthModel.name || createUpdateGrowthModel}
          setCreateUpdateInput={setCreateUpdateGrowthModel}
        />
      </Stack>
    </form>
  );
}

export default GrowthModel;

const getSGRFile = (modelName: string, method?: methodType, growthModelId?: string, planId?: string) => {
  const defaultFileName = 'SGR-template';

  if (method === methodType.edit && growthModelId) {
    const url = `${BASE_URL}/growthModels/${growthModelId}/download?planId=${planId}`;
    const fileName = modelName || defaultFileName;
    return {url, fileName};
  } else {
    const url = `${BASE_URL}/SGRTemplate/`;
    return {url, fileName: defaultFileName};
  }
};

const ScrollableDiv = styled('div')(() => ({
  maxHeight: '300px',
  overflowY: 'auto',
}));

function initialAdjustments(adjustmentType: GrowthModelResponseAdjustmentTypeEnum) {
  switch (adjustmentType) {
    case GrowthModelResponseAdjustmentTypeEnum.Constant:
      return [0];
    case GrowthModelResponseAdjustmentTypeEnum.MonthsSinceRelease:
      return emptyAdjustments(MONTHS_AFTER_STOCKING);
    case GrowthModelResponseAdjustmentTypeEnum.CalendarYear:
      return emptyAdjustments(CALENDER_YEAR);
  }
}

const emptyAdjustments = (periods: number) => Array.from({length: periods}, () => 0);
